import { useQuery } from "@tanstack/react-query";
import { dashboardKeys } from "@/lib/query";

// Mirrors the server response from src/api/dashboard-insights.ts. Types are
// co-located with the hook (per frontend.md) rather than imported from the
// server module.

export type ExpiringBucket = "expired" | "within_30" | "within_60";

export interface ExpiringCertItem {
  id: string;
  personId: string;
  personName: string;
  certificationName: string;
  expiresAt: string;
  bucket: ExpiringBucket;
}

export interface ExpiringCertsInsight {
  counts: { expired: number; within30: number; within60: number };
  items: ExpiringCertItem[];
}

export interface OrgHealthInsight {
  headcountByStatus: { active: number; inactive: number; unknown: number };
  headcountByEmploymentType: { fullTime: number; partTime: number; contractor: number };
  rolesWithNoPeople: number;
  assignedNotStarted: number;
  /** Active people whose manager is inactive — stale reporting lines (WI-076). */
  inactiveManagerCount: number;
}

export interface ReportCertSummary {
  expiringSoon: number;
  unstarted: number;
}

export interface NewJoinerItem {
  id: string;
  name: string;
  title: string | null;
  avatarUrl: string | null;
  /** Most recent of start/rehire date (`YYYY-MM-DD`). */
  joinedAt: string;
  rehire: boolean;
}

export interface DashboardInsights {
  scope: "admin" | "manager" | "user";
  expiringCerts: ExpiringCertsInsight;
  orgHealth?: OrgHealthInsight;
  reportCertSummary?: Record<string, ReportCertSummary>;
  newJoiners: NewJoinerItem[];
}

export function useDashboardInsights() {
  return useQuery({
    queryKey: dashboardKeys.insights,
    queryFn: async (): Promise<DashboardInsights> => {
      const res = await fetch("/api/dashboard/insights");
      if (!res.ok) throw new Error("Failed to fetch dashboard insights");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface AssignedNotStartedItem {
  id: string;
  personId: string;
  personName: string;
  certificationName: string;
  assignedAt: string | null;
}

interface NotStartedCertsResponse {
  items: AssignedNotStartedItem[];
  capped: boolean;
}

/** The records behind the org-health "Certs not started" tile (admin-only). */
export function useNotStartedCerts() {
  return useQuery({
    queryKey: dashboardKeys.notStartedCerts,
    queryFn: async (): Promise<NotStartedCertsResponse> => {
      const res = await fetch("/api/dashboard/not-started-certs");
      if (!res.ok) throw new Error("Failed to fetch not-started certifications");
      const json = await res.json();
      return json.data;
    },
  });
}
